import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import { AnimFloatIdle } from '@readdy/anim/card/float-idle/react';

const plans = [
  {
    name: 'Plan Básico',
    price: '270 USD',
    badge: 'Ideal para Emprendedores',
    description: 'Tu primer paso para tener presencia profesional en Internet y comenzar a captar clientes.',
    includes: [
      'Hosting por 1 año',
      'Dominio por 1 año',
      'Landing Page Profesional',
      'Meta Pixel',
      'API de Conversiones',
      '1 Campaña Meta Ads',
      '4 Creativos con IA',
    ],
    benefits: [
      'Presencia profesional',
      'Captación de clientes',
      'Sitio optimizado',
      'Preparado para Meta Ads',
    ],
    highlighted: false,
    cta: 'Quiero este Plan',
  },
  {
    name: 'Plan Pro',
    price: '487 USD',
    badge: '⭐ Más Vendido',
    description: 'La mejor opción para empresas que buscan crecer con un sitio web más completo y preparado para vender.',
    includes: [
      'Todo lo del Plan Básico',
      'Página Principal',
      '2 Subpáginas',
      'Carrito de Compras',
      'Pixel + API',
      '2 Campañas Meta Ads',
      '4 Videos IA',
    ],
    benefits: [
      'Mayor autoridad',
      'Más conversiones',
      'Más posicionamiento',
      'Más oportunidades de venta',
    ],
    highlighted: true,
    cta: 'Elegir Plan Pro',
  },
  {
    name: 'Plan Master',
    price: '647 USD',
    badge: '👑 Ecosistema Completo',
    description: 'La solución más completa para empresas que desean construir un ecosistema digital preparado para vender todos los días.',
    includes: [
      'Todo lo del Plan Pro',
      'Páginas ilimitadas',
      'Google Ads',
      'Meta Ads',
      'Pixel',
      'API',
      'SEO',
      '6 Videos IA',
      'Automatizaciones',
    ],
    benefits: [
      'Mayor visibilidad',
      'Más clientes',
      'Sistema escalable',
      'Ecosistema Digital Completo',
    ],
    highlighted: false,
    cta: 'Quiero el Plan Master',
  },
];

const comparisonRows = [
  { label: 'Hosting', values: ['✅', '✅', '✅'] },
  { label: 'Dominio', values: ['✅', '✅', '✅'] },
  { label: 'Landing Page', values: ['✅', '✅', '✅'] },
  { label: 'Subpáginas', values: ['—', '2', 'Ilimitadas'] },
  { label: 'Páginas Ilimitadas', values: ['—', '—', '✅'] },
  { label: 'Meta Ads', values: ['1 Campaña', '2 Campañas', '✅'] },
  { label: 'Google Ads', values: ['—', '—', '✅'] },
  { label: 'Pixel', values: ['✅', '✅', '✅'] },
  { label: 'API', values: ['✅', '✅', '✅'] },
  { label: 'Carrito de Compras', values: ['—', '✅', '✅'] },
  { label: 'SEO', values: ['—', '—', '✅'] },
  { label: 'Videos IA', values: ['2', '4', '6'] },
  { label: 'Automatización', values: ['—', '—', '✅'] },
];

const whyUsCards = [
  { icon: 'ri-palette-line', title: 'Diseño Moderno', desc: 'Interfaces limpias, elegantes y alineadas con tu marca que generan confianza desde el primer segundo.' },
  { icon: 'ri-line-chart-line', title: 'Optimización para Conversión', desc: 'Cada elemento está estratégicamente diseñado para convertir visitantes en clientes.' },
  { icon: 'ri-search-eye-line', title: 'SEO Integrado', desc: 'Estructura optimizada para que Google encuentre y posicione tu sitio por encima de tu competencia.' },
  { icon: 'ri-flashlight-line', title: 'Carga Ultra Rápida', desc: 'Tiempos de carga inferiores a 2 segundos para una experiencia fluida y sin abandonos.' },
  { icon: 'ri-smartphone-line', title: '100% Responsive', desc: 'Tu sitio se ve y funciona perfecto en cualquier dispositivo: móvil, tablet y escritorio.' },
  { icon: 'ri-rocket-line', title: 'Preparado para Crecer', desc: 'Arquitectura escalable que te permite añadir funcionalidades sin reconstruir desde cero.' },
];

const processSteps = [
  { step: '01', title: 'Reunión Estratégica', desc: 'Analizamos tu negocio, objetivos y competencia para definir la mejor estrategia digital.' },
  { step: '02', title: 'Diseño UX/UI', desc: 'Creamos wireframes y prototipos visuales con un diseño premium enfocado en la conversión.' },
  { step: '03', title: 'Desarrollo', desc: 'Construimos tu sitio con las mejores tecnologías, optimizado para velocidad y rendimiento.' },
  { step: '04', title: 'Integraciones', desc: 'Conectamos Pixel, API, CRM, pasarelas de pago y todas las herramientas que necesitas.' },
  { step: '05', title: 'Pruebas', desc: 'Revisamos cada detalle: velocidad, responsive, funcionalidad y experiencia de usuario.' },
  { step: '06', title: 'Entrega', desc: 'Tu sitio sale al mundo. Te entregamos acceso completo y capacitación para que lo administres.' },
];

const faqs = [
  { q: '¿Cuánto tiempo toma crear mi sitio web?', a: 'Dependiendo del plan que elijas, tu sitio estará listo entre 4 días hábiles. En la reunión estratégica definimos un cronograma exacto según tus necesidades.' },
  { q: '¿Necesito conocimientos técnicos para administrar el sitio?', a: 'No. Te entregamos el sitio con un panel de administración intuitivo y te capacitamos para que puedas hacer cambios básicos sin depender de un desarrollador.' },
  { q: '¿Incluye el dominio y hosting?', a: 'Sí. Todos los planes incluyen hosting y dominio por el primer año. En el segundo año se debe hacer mantenimiento de la página web y ello incluye de nuevo hosting y dominio.' },
  { q: '¿Puedo actualizar mi plan más adelante?', a: 'Por supuesto. Puedes comenzar con el Plan Básico y migrar al Plan Pro o Master cuando tu negocio necesite más funcionalidades. Solo pagas la diferencia.' },
  { q: '¿Las campañas de Meta Ads las configuran ustedes?', a: 'Sí. Diseñamos, configuramos y optimizamos las campañas para que empieces a recibir tráfico de calidad desde el primer día.' },
  { q: '¿Ofrecen soporte después de la entrega?', a: 'Sí. Todos los planes incluyen 30 días de soporte técnico gratuito después de la entrega. Además, ofrecemos planes de mantenimiento mensual.' },
  { q: '¿El sitio web es compatible con pasarelas de pago?', a: 'Los planes Pro y Master incluyen carrito de compras y conexión con pasarelas de pago.&nbsp;' },
];

export default function WebDesign() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleFaq = (i: number) => {
    setOpenFaq(openFaq === i ? null : i);
  };

  const handleQuoteSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');
    try {
      const res = await fetch('https://readdy.ai/api/form/d967l1n0d76aer3t5160', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* not JSON */ }
      const code = (parsed as { code?: string }).code;
      if (res.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string } })?.meta?.message || (parsed as { message?: string })?.message || text;
        setFormError(typeof serverMsg === 'string' ? serverMsg : 'Ocurrió un error. Intenta de nuevo.');
        setFormStatus('error');
      }
    } catch {
      setFormError('Error de conexión. Intenta de nuevo.');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* HERO */}
        <section className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-accent-500/[0.02] rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary-500/[0.02] rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>
          <div className="mx-auto max-w-[1400px] w-full px-6 md:px-10">
            <div className="flex flex-col items-center text-center">
              <ScrollReveal direction="up">
                <div className="relative w-full max-w-5xl mb-10 md:mb-14">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent-500/10 via-transparent to-primary-500/8 rounded-3xl blur-2xl"></div>
                  <div className="relative rounded-2xl overflow-hidden border border-background-200 shadow-[0_8px_40px_rgba(0,0,0,0.06)] bg-white">
                    <video
                      src="https://storage.helloreaddy.io/project_files/3dceec99-0a90-4550-9724-ce762c1f57f1/8cd91063-8a47-42af-bc1a-6518fa096d15_DANIELA.mp4"
                      className="w-full h-auto object-contain"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                    />
                  </div>
                </div>
              </ScrollReveal>
              <div className="max-w-[680px]">
                <ScrollReveal direction="up">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 border border-primary-200/50 text-primary-700 text-xs font-semibold tracking-wide mb-6">
                    <i className="ri-global-line text-sm"></i>
                    Diseño Web Profesional
                  </span>
                </ScrollReveal>
                <ScrollReveal direction="up" delay={0.1}>
                  <h1 className="font-heading text-3xl md:text-5xl lg:text-[3.25rem] font-bold text-foreground-950 leading-[1.5] tracking-tight mb-5">
                    Creamos páginas web que convierten visitantes en clientes.
                  </h1>
                </ScrollReveal>
                <ScrollReveal direction="up" delay={0.15}>
                  <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-8">
                    No solo diseñamos sitios web.<br />
                    Construimos una presencia digital profesional preparada para atraer clientes, generar confianza y aumentar las ventas.
                  </p>
                </ScrollReveal>
                <ScrollReveal direction="up" delay={0.2}>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href="#planes"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap shadow-[0_4px_24px_rgba(200,30,30,0.3)] hover:shadow-[0_6px_32px_rgba(200,30,30,0.4)] hover:-translate-y-0.5 cursor-pointer"
                    >
                      Solicitar Cotización
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://wa.link/em25v1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white border-2 border-primary-500 text-primary-600 font-semibold text-sm hover:bg-primary-50 hover:border-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-lg"></i>
                      Hablar por WhatsApp
                    </a>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* PLANS */}
        <section id="planes" className="py-16 md:py-24 bg-white">
          <div className="mx-auto max-w-[1200px] w-full px-6 md:px-10">
            <div className="text-center mb-14">
              <ScrollReveal direction="up">
                <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">Nuestros Planes</span>
              </ScrollReveal>
              <ScrollReveal direction="up" delay={0.1}>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
                  Elige la solución ideal según el nivel de crecimiento de tu empresa
                </h2>
              </ScrollReveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
              {plans.map((plan, i) => (
                <ScrollReveal key={plan.name} direction="up" delay={0.1 * i} className="h-full">
                  <div className={`relative flex flex-col h-full rounded-2xl p-6 md:p-8 border transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-white border-primary-500 shadow-[0_20px_60px_rgba(200,30,30,0.10)]'
                      : 'bg-white border-background-200 hover:border-background-300 hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]'
                  }`}>
                    <div className="text-center mb-6">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full mb-4 ${
                        plan.highlighted
                          ? 'bg-primary-500 text-white'
                          : 'bg-accent-50 text-accent-700'
                      }`}>
                        {plan.badge}
                      </span>
                      <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">{plan.name}</h3>
                      <div className="font-heading text-4xl md:text-5xl font-extrabold text-primary-500 mb-2">
                        {plan.price}
                      </div>
                      <p className="text-foreground-500 text-sm leading-relaxed max-w-[260px] mx-auto">{plan.description}</p>
                    </div>
                    <div className="border-t border-background-200 pt-6 mb-6 flex-1">
                      <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">Incluye</p>
                      <ul className="space-y-3">
                        {plan.includes.map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-sm text-foreground-700">
                            <i className="ri-check-line text-primary-500 text-base mt-0.5 shrink-0"></i>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="border-t border-background-200 pt-5 mb-8">
                      <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Beneficios</p>
                      <div className="flex flex-wrap gap-2">
                        {plan.benefits.map((b) => (
                          <span key={b} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-medium">
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>
                    <a
                      href="https://wa.link/em25v1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`mt-auto block text-center py-3.5 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        plan.highlighted
                          ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-[0_4px_20px_rgba(200,30,30,0.3)]'
                          : 'bg-foreground-950 text-white hover:bg-foreground-800'
                      }`}
                    >
                      {plan.cta}
                      <i className="ri-arrow-right-line ml-2"></i>
                    </a>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* COMPARISON TABLE */}
        <section className="py-16 md:py-24 bg-white">
          <div className="mx-auto max-w-[1000px] w-full px-6 md:px-10">
            <div className="text-center mb-12">
              <ScrollReveal direction="up">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Comparativa de <span className="text-red-600">Planes</span></h2>
              </ScrollReveal>
              <ScrollReveal direction="up" delay={0.1}>
                <p className="text-foreground-500 text-base">Compara las características de cada plan y elige el que mejor se adapte a tu empresa.</p>
              </ScrollReveal>
            </div>
            <ScrollReveal direction="up" delay={0.15}>
              <div className="overflow-x-auto rounded-2xl border-2 border-red-200 shadow-[0_4px_24px_rgba(200,30,30,0.08)]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-red-500 to-red-600">
                      <th className="text-left py-4 px-6 font-semibold text-white text-sm">Característica</th>
                      <th className="text-center py-4 px-4 font-semibold text-white/90 text-sm">Plan Básico</th>
                      <th className="text-center py-4 px-4 font-bold text-white text-sm bg-red-700/30">Plan Pro</th>
                      <th className="text-center py-4 px-4 font-semibold text-white/90 text-sm">Plan Master</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map((row, i) => (
                      <tr key={row.label} className={`border-t border-red-100 ${i % 2 === 0 ? 'bg-white' : 'bg-red-50/30'}`}>
                        <td className="py-3.5 px-6 font-medium text-foreground-700">{row.label}</td>
                        <td className="text-center py-3.5 px-4 text-foreground-600">{row.values[0]}</td>
                        <td className={`text-center py-3.5 px-4 font-medium bg-red-50/60 ${row.values[1] === '✅' ? 'text-red-600' : 'text-foreground-600'}`}>{row.values[1]}</td>
                        <td className={`text-center py-3.5 px-4 ${row.values[2] === '✅' ? 'text-red-600 font-medium' : 'text-foreground-600'}`}>{row.values[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* WHY CHOOSE US */}
        <section className="py-16 md:py-24 bg-background-950">
          <div className="mx-auto max-w-[1200px] w-full px-6 md:px-10">
            <div className="text-center mb-14">
              <ScrollReveal direction="up">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">¿Por qué elegir nuestros sitios web?</h2>
              </ScrollReveal>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyUsCards.map((card, i) => {
                const cardContent = (
                  <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-7 hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)] transition-shadow duration-300">
                    <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center mb-4">
                      <i className={`${card.icon} text-primary-500 text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">{card.title}</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">{card.desc}</p>
                  </div>
                );
                return (
                  <ScrollReveal key={card.title} direction="up" delay={0.08 * i}>
                    {prefersReducedMotion ? cardContent : <AnimFloatIdle duration={3000}>{cardContent}</AnimFloatIdle>}
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="mx-auto max-w-[1000px] w-full px-6 md:px-10">
            <div className="text-center mb-14">
              <ScrollReveal direction="up">
                <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">Proceso de Trabajo</span>
              </ScrollReveal>
              <ScrollReveal direction="up" delay={0.1}>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Así creamos tu sitio web</h2>
              </ScrollReveal>
            </div>
            <div className="relative">
              <div className="absolute left-[27px] top-0 bottom-0 w-px bg-background-300 hidden md:block"></div>
              <div className="space-y-0">
                {processSteps.map((s, i) => (
                  <ScrollReveal key={s.step} direction="up" delay={0.08 * i}>
                    <div className="flex gap-5 md:gap-8 pb-8 last:pb-0">
                      <div className="relative z-10 shrink-0">
                        <div className="w-14 h-14 rounded-2xl bg-primary-500 text-white flex items-center justify-center font-heading text-lg font-bold shadow-[0_4px_16px_rgba(200,30,30,0.2)]">
                          {i + 1}
                        </div>
                      </div>
                      <div className="pt-2">
                        <h3 className="font-heading text-lg font-bold text-foreground-950 mb-1">{s.title}</h3>
                        <p className="text-foreground-500 text-sm leading-relaxed max-w-[500px]">{s.desc}</p>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="mx-auto max-w-[800px] w-full px-6 md:px-10">
            <div className="text-center mb-12">
              <ScrollReveal direction="up">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Preguntas Frecuentes</h2>
              </ScrollReveal>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <ScrollReveal key={i} direction="up" delay={0.04 * i}>
                  <div className="bg-white rounded-2xl border border-background-200 overflow-hidden">
                    <button
                      onClick={() => toggleFaq(i)}
                      className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 cursor-pointer"
                    >
                      <span className="font-semibold text-foreground-900 text-sm pr-4">{faq.q}</span>
                      <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-foreground-400 text-lg shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaq === i && (
                      <div className="px-6 pb-5 text-foreground-600 text-sm leading-relaxed border-t border-background-100 pt-4">
                        {faq.a}
                      </div>
                    )}
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* QUOTE FORM */}
        <section className="py-16 md:py-24 bg-white">
          <div className="mx-auto max-w-[700px] w-full px-6 md:px-10">
            <div className="text-center mb-10">
              <ScrollReveal direction="up">
                <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">Solicitar Cotización</span>
              </ScrollReveal>
              <ScrollReveal direction="up" delay={0.1}>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
                  Cuéntanos sobre tu proyecto
                </h2>
              </ScrollReveal>
              <ScrollReveal direction="up" delay={0.15}>
                <p className="text-foreground-500 text-base">Déjanos tus datos y te enviaremos una cotización personalizada en menos de 24 horas.</p>
              </ScrollReveal>
            </div>
            <ScrollReveal direction="up" delay={0.2}>
              <form onSubmit={handleQuoteSubmit} data-readdy-form="true" className="bg-white rounded-2xl border border-background-200 shadow-[0_4px_24px_rgba(0,0,0,0.04)] p-8 md:p-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Nombre</label>
                    <input type="text" name="name" required className="w-full px-4 py-3 rounded-xl border border-background-300 bg-white text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all" placeholder="Tu nombre" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Email</label>
                    <input type="email" name="email" required className="w-full px-4 py-3 rounded-xl border border-background-300 bg-white text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all" placeholder="tu@email.com" />
                  </div>
                </div>
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Teléfono</label>
                  <input type="tel" name="phone" required className="w-full px-4 py-3 rounded-xl border border-background-300 bg-white text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all" placeholder="+57 300 123 4567" />
                </div>
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Plan de interés</label>
                  <select name="plan" className="w-full px-4 py-3 rounded-xl border border-background-300 bg-white text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all appearance-none cursor-pointer">
                    <option value="">Selecciona un plan</option>
                    <option value="basico">Plan Básico — 270 USD</option>
                    <option value="pro">Plan Pro — 487 USD</option>
                    <option value="master">Plan Master — 647 USD</option>
                    <option value="otro">No estoy seguro / Otro</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Cuéntanos sobre tu proyecto</label>
                  <textarea name="message" rows={4} maxLength={500} className="w-full px-4 py-3 rounded-xl border border-background-300 bg-white text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all resize-none" placeholder="Describe tu negocio y lo que necesitas..."></textarea>
                  <p className="text-xs text-foreground-400 mt-1">Máximo 500 caracteres</p>
                </div>
                <div className="honeypot-field">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button type="submit" className="w-full py-4 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 shadow-[0_4px_20px_rgba(200,30,30,0.3)] hover:shadow-[0_6px_28px_rgba(200,30,30,0.4)] whitespace-nowrap cursor-pointer">
                  Enviar Solicitud
                  <i className="ri-send-plane-line ml-2"></i>
                </button>
                {formStatus === 'success' && (
                  <div className="mt-4 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm text-center">
                    ¡Gracias! Recibimos tu solicitud. Te contactaremos en menos de 24 horas.
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm text-center">
                    {formError || 'Ocurrió un error. Intenta de nuevo.'}
                  </div>
                )}
              </form>
            </ScrollReveal>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 md:py-24 bg-white relative overflow-hidden">
          <div className="mx-auto max-w-[800px] w-full px-6 md:px-10 text-center">
            <ScrollReveal direction="up">
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4 leading-[2.6]">
                Construimos páginas web diseñadas para hacer crecer tu negocio
              </h2>
            </ScrollReveal>
            <ScrollReveal direction="up" delay={0.1}>
              <p className="text-foreground-500 text-lg mb-10 max-w-[560px] mx-auto">
                Elige el plan que mejor se adapte a tu negocio y empieza a atraer clientes con una presencia digital profesional.
              </p>
            </ScrollReveal>
            <ScrollReveal direction="up" delay={0.15}>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="#planes"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap shadow-[0_4px_24px_rgba(200,30,30,0.3)] hover:shadow-[0_6px_32px_rgba(200,30,30,0.4)] hover:-translate-y-0.5 cursor-pointer"
                >
                  Solicitar Cotización
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="https://wa.link/em25v1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white border-2 border-primary-500 text-primary-600 font-semibold text-sm hover:bg-primary-50 hover:border-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Hablar por WhatsApp
                </a>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}